<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouterStore } from '@/store/router'
import { useEstudios } from '@/composables/useEstudios'
import { usePublicTheme } from '@/composables/usePublicTheme'

const themeStore = usePublicTheme()
const route = useRouterStore()
const token = computed(() => route.params.token)
const { fetchPublicEstudioAsignacion, updatePublicAsignacion, addPublicEstudioNota } = useEstudios()

const TIPO_LABELS = {
  visita_inicial: 'Visita inicial',
  revisita: 'Revisita',
  estudio_iniciado: 'Estudio iniciado',
  estudio_establecido: 'Estudio establecido',
}
const URGENCIA_LABELS = { 1: 'Baja', 2: 'Media-baja', 3: 'Media', 4: 'Alta', 5: 'Urgente' }

const loading = ref(true)
const errorMsg = ref(null)
const estudio = ref(null)
const notas = ref([])

const load = async () => {
  loading.value = true
  errorMsg.value = null
  if (!token.value) {
    errorMsg.value = 'Enlace inválido: falta el token.'
    loading.value = false
    return
  }
  const res = await fetchPublicEstudioAsignacion(token.value)
  if (res.error) {
    errorMsg.value = 'Este enlace no es válido o fue eliminado.'
  } else {
    estudio.value = res.estudio
    notas.value = res.notas || []
    document.title = 'Estudio asignado'
  }
  loading.value = false
}

// ─── Completar quién se hace cargo (sólo si todavía no estaba asignado) ─────
const asignacionForm = ref({ asignadoA: '', asignadoAWhatsapp: '' })
const guardandoAsignacion = ref(false)
const errorAsignacion = ref(null)

const guardarAsignacion = async () => {
  if (!asignacionForm.value.asignadoA.trim()) {
    errorAsignacion.value = 'Indicá quién se hace cargo del estudio.'
    return
  }
  guardandoAsignacion.value = true
  errorAsignacion.value = null
  try {
    const res = await updatePublicAsignacion(token.value, asignacionForm.value)
    if (res.error) {
      errorAsignacion.value = res.error
      return
    }
    estudio.value = {
      ...estudio.value,
      asignadoA: asignacionForm.value.asignadoA.trim(),
      asignadoAWhatsapp: asignacionForm.value.asignadoAWhatsapp.trim(),
      asignado: true,
    }
  } finally {
    guardandoAsignacion.value = false
  }
}

// ─── Notas de seguimiento (visitas y resultados) ────────────────────────────
const hoy = () => new Date().toISOString().slice(0, 10)
const notaForm = ref({ fecha: hoy(), nota: '' })
const guardandoNota = ref(false)
const errorNota = ref(null)

const agregarNota = async () => {
  if (!notaForm.value.nota.trim()) {
    errorNota.value = 'Escribí una nota antes de guardar.'
    return
  }
  guardandoNota.value = true
  errorNota.value = null
  try {
    const res = await addPublicEstudioNota(token.value, notaForm.value)
    if (res.error) {
      errorNota.value = res.error
      return
    }
    notas.value = res.notas || []
    notaForm.value = { fecha: hoy(), nota: '' }
  } finally {
    guardandoNota.value = false
  }
}

const formatFecha = (f) => {
  if (!f) return ''
  const [y, m, d] = f.split('-')
  return y && m && d ? `${d}/${m}/${y}` : f
}

const formatFechaHora = (iso) => {
  if (!iso) return ''
  const d = new Date(iso.replace(' ', 'T') + 'Z')
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}

onMounted(load)
</script>

<template>
  <div class="simple-page">
    <button
      type="button"
      class="theme-toggle-btn"
      :title="themeStore.theme === 'dark' ? 'Modo claro' : 'Modo oscuro'"
      @click="themeStore.toggle()"
    >
      <i :class="themeStore.theme === 'dark' ? 'pi pi-sun' : 'pi pi-moon'" />
    </button>

    <img src="/territorios-icono.png" alt="Logo" class="simple-logo">

    <div v-if="loading" class="simple-loading">
      <i class="pi pi-spin pi-spinner" />
    </div>

    <div v-else-if="errorMsg" class="simple-error">{{ errorMsg }}</div>

    <template v-else>
      <h1 class="simple-title">Estudio asignado</h1>

      <!-- Datos del estudio (sólo lectura) -->
      <div class="simple-info-card">
        <div class="simple-info-row">
          <span class="simple-info-label">Estudiante</span>
          <span class="simple-info-value">
            {{ estudio.estudiante || '-' }}
            <a
              v-if="estudio.estudianteWhatsapp"
              :href="`https://wa.me/${String(estudio.estudianteWhatsapp).replace(/\D/g, '')}`"
              target="_blank"
              rel="noopener"
            ><i class="pi pi-whatsapp" /> {{ estudio.estudianteWhatsapp }}</a>
          </span>
        </div>
        <div class="simple-info-row">
          <span class="simple-info-label">Publicador que lo cargó</span>
          <span class="simple-info-value">
            {{ estudio.publicador || '-' }}
            <a
              v-if="estudio.whatsapp"
              :href="`https://wa.me/${String(estudio.whatsapp).replace(/\D/g, '')}`"
              target="_blank"
              rel="noopener"
            ><i class="pi pi-whatsapp" /> {{ estudio.whatsapp }}</a>
          </span>
        </div>
        <div v-if="estudio.direccion" class="simple-info-row">
          <span class="simple-info-label">Dirección</span>
          <span class="simple-info-value">
            {{ estudio.direccion }}
            <a
              v-if="estudio.lat && estudio.lng"
              :href="`https://www.google.com/maps?q=${estudio.lat},${estudio.lng}`"
              target="_blank"
              rel="noopener"
            >ver mapa</a>
          </span>
        </div>
        <div v-if="estudio.fechaInicio" class="simple-info-row">
          <span class="simple-info-label">Fecha de inicio</span>
          <span class="simple-info-value">{{ formatFecha(estudio.fechaInicio) }}</span>
        </div>
        <div class="simple-info-row">
          <span class="simple-info-label">Tipo</span>
          <span class="simple-info-value">{{ TIPO_LABELS[estudio.tipo] || estudio.tipo }}</span>
        </div>
        <div v-if="estudio.urgencia" class="simple-info-row">
          <span class="simple-info-label">Urgencia</span>
          <span class="simple-info-value">{{ URGENCIA_LABELS[estudio.urgencia] || estudio.urgencia }}</span>
        </div>
        <div v-if="estudio.aQuienSePaso" class="simple-info-row">
          <span class="simple-info-label">A quién se lo pasó</span>
          <span class="simple-info-value">{{ estudio.aQuienSePaso }}</span>
        </div>
        <div v-if="estudio.observaciones" class="simple-info-row">
          <span class="simple-info-label">Observaciones</span>
          <span class="simple-info-value simple-item-obs">{{ estudio.observaciones }}</span>
        </div>
      </div>

      <!-- Quién se hace cargo del estudio -->
      <div v-if="estudio.asignadoA" class="simple-info-card simple-asignado-card">
        <div class="simple-info-row">
          <span class="simple-info-label">Asignado a</span>
          <span class="simple-info-value">
            {{ estudio.asignadoA }}
            <a
              v-if="estudio.asignadoAWhatsapp"
              :href="`https://wa.me/${String(estudio.asignadoAWhatsapp).replace(/\D/g, '')}`"
              target="_blank"
              rel="noopener"
            ><i class="pi pi-whatsapp" /> {{ estudio.asignadoAWhatsapp }}</a>
          </span>
        </div>
      </div>
      <form v-else class="simple-form" @submit.prevent="guardarAsignacion">
        <div class="simple-hint">Todavía nadie completó quién se hace cargo de este estudio.</div>

        <label class="simple-field-label mt" for="asig-nombre">Tu nombre</label>
        <input
          id="asig-nombre"
          v-model="asignacionForm.asignadoA"
          type="text"
          class="simple-input"
          placeholder="Quién se hace cargo del estudio"
        >

        <label class="simple-field-label mt" for="asig-whatsapp">Tu WhatsApp <span class="simple-optional">(opcional)</span></label>
        <input
          id="asig-whatsapp"
          v-model="asignacionForm.asignadoAWhatsapp"
          type="tel"
          class="simple-input"
          placeholder="+54 9 264 555 5555"
        >

        <div v-if="errorAsignacion" class="simple-error-msg">{{ errorAsignacion }}</div>

        <button type="submit" class="simple-btn" :disabled="guardandoAsignacion">
          {{ guardandoAsignacion ? 'Guardando...' : 'Confirmar que me hago cargo' }}
        </button>
      </form>

      <!-- Notas de seguimiento: visitas y resultados -->
      <div class="simple-notas-section">
        <div class="simple-section-title">Notas de seguimiento</div>

        <form class="simple-form simple-nota-form" @submit.prevent="agregarNota">
          <label class="simple-field-label" for="nota-fecha">Fecha</label>
          <input id="nota-fecha" v-model="notaForm.fecha" type="date" class="simple-input">

          <label class="simple-field-label mt" for="nota-texto">Nota <span class="simple-optional">(visita, resultado, etc.)</span></label>
          <textarea
            id="nota-texto"
            v-model="notaForm.nota"
            class="simple-input simple-textarea"
            rows="3"
            placeholder="Ej: Visité y no estaba, quedamos para otro día..."
          />

          <div v-if="errorNota" class="simple-error-msg">{{ errorNota }}</div>

          <button type="submit" class="simple-btn simple-btn-add" :disabled="guardandoNota">
            {{ guardandoNota ? 'Guardando...' : 'Agregar nota' }}
          </button>
        </form>

        <div v-if="!notas.length" class="simple-hint">Todavía no hay notas cargadas.</div>
        <div v-else class="simple-list">
          <div v-for="n in notas" :key="n.id" class="simple-nota-item">
            <div class="simple-nota-fecha">{{ n.fecha ? formatFecha(n.fecha) : formatFechaHora(n.createdAt) }}</div>
            <div class="simple-nota-texto">{{ n.nota }}</div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped>
.simple-page {
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 20px 48px;
  background: var(--bg-grouped, #f2f2f7);
  text-align: center;
}

.theme-toggle-btn {
  position: fixed;
  top: max(env(safe-area-inset-top, 0px), 16px);
  right: 16px;
  z-index: 10;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 1.5px solid var(--separator, rgba(0, 0, 0, 0.15));
  background: var(--bg-primary, #fff);
  color: var(--label-secondary, #666);
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.theme-toggle-btn:active {
  opacity: 0.7;
}

.simple-logo {
  width: 112px;
  height: 112px;
  margin-bottom: 28px;
}

.simple-loading {
  font-size: 2rem;
  color: var(--accent, #007aff);
  margin-top: 40px;
}

.simple-error {
  font-size: 18px;
  color: var(--label-secondary, #666);
  margin-top: 20px;
  max-width: 420px;
}

.simple-title {
  font-size: 26px;
  font-weight: 700;
  color: var(--label-primary, #111);
  margin: 0 0 16px;
}

.simple-hint {
  width: 100%;
  max-width: 420px;
  background: var(--info-soft, #e6f2ff);
  color: var(--label-primary, #333);
  border-radius: 12px;
  padding: 14px 16px;
  font-size: 14px;
  line-height: 1.4;
  margin-top: 4px;
  margin-bottom: 12px;
  text-align: left;
}

.simple-info-card {
  width: 100%;
  max-width: 420px;
  background: var(--bg-primary, #fff);
  border-radius: 14px;
  padding: 14px 16px;
  text-align: left;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  margin-bottom: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.simple-asignado-card {
  background: var(--success-soft, #e3f7e8);
}

.simple-info-row {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.simple-info-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--label-secondary, #666);
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.simple-info-value {
  font-size: 15px;
  color: var(--label-primary, #111);
  line-height: 1.4;
}

.simple-info-value a {
  color: var(--accent, #007aff);
  margin-left: 6px;
}

.simple-item-obs {
  white-space: pre-wrap;
}

.simple-form {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 420px;
  text-align: left;
}

.simple-field-label {
  font-size: 15px;
  font-weight: 600;
  color: var(--label-secondary, #666);
  margin-bottom: 8px;
}

.simple-field-label.mt {
  margin-top: 22px;
}

.simple-input {
  width: 100%;
  box-sizing: border-box;
  border-radius: 12px;
  border: 1.5px solid rgba(0, 0, 0, 0.15);
  background: var(--bg-primary, #fff);
  padding: 14px 16px;
  font-size: 17px;
  color: var(--label-primary, #111);
}

.simple-textarea {
  resize: vertical;
  font-family: inherit;
}

.simple-optional {
  font-weight: 400;
  color: var(--label-tertiary, #999);
}

.simple-error-msg {
  margin-top: 16px;
  color: var(--danger, #ff3b30);
  font-size: 15px;
  text-align: center;
}

.simple-btn {
  margin-top: 20px;
  width: 100%;
  padding: 18px;
  border: none;
  border-radius: 14px;
  background: var(--success, #34c759);
  color: #fff;
  font-size: 17px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  -webkit-tap-highlight-color: transparent;
}

.simple-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.simple-btn-add {
  background: var(--accent, #007aff);
}

.simple-notas-section {
  width: 100%;
  max-width: 420px;
  margin-top: 8px;
}

.simple-section-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--label-primary, #111);
  margin-bottom: 12px;
  text-align: left;
}

.simple-nota-form {
  max-width: none;
  margin-bottom: 18px;
}

.simple-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.simple-nota-item {
  background: var(--bg-primary, #fff);
  border-radius: 12px;
  padding: 12px 14px;
  text-align: left;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.simple-nota-fecha {
  font-size: 12px;
  font-weight: 600;
  color: var(--label-secondary, #666);
  margin-bottom: 4px;
}

.simple-nota-texto {
  font-size: 15px;
  color: var(--label-primary, #111);
  white-space: pre-wrap;
  line-height: 1.4;
}
</style>
